import { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useMotionValueEvent, useScroll } from 'motion/react'
import { ButtonNextLink } from '@/components/ButtonNextLink'
import { Pillow } from '@/components/Pillow/Pillow'
import { useProductData } from '@/components/ProductData/ProductDataProvider'
import { Heading } from 'ui/src/components/Heading/Heading'
import { Text } from 'ui/src/components/Text/Text'
import { CrossIcon } from 'ui/src/icons/CrossIcon'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { useHasOffersFromMultipleTiers } from '../../PurchaseForm/sseHasOffersFromMultipleTiers'
import { useSelectedOffer } from '../../PurchaseForm/useSelectedOffer'
import { usePriceCalculatorStep } from '../../usePriceCalculatorStep'
import { ProgressBar } from '../ProgressBar/ProgressBar'
import { SelectedOfferAnimatedPrice } from '../SelectedOfferAnimatedPrice/SelectedOfferAnimatedPrice'
import { useCondensedProductHero } from '../useCondensedProductHero'
import {
  mobileCloseButton,
  stickyProductHeader,
  stickyProductHeaderContent,
  subTypeLabel,
} from './StickyProductHeader.css'

const HERO_TRESHOLD = 250

type Props = {
  setIsCondensed: (isCondensed: boolean) => void
}

export function StickyProductHeader({ setIsCondensed }: Props) {
  const [selectedOffer] = useSelectedOffer()
  const productData = useProductData()
  const { scrollY } = useScroll()
  const { step } = usePriceCalculatorStep()

  const [scrolledPast, setScrolledPast] = useState(false)
  const isCondensedProductHero = useCondensedProductHero()

  const hasTiers = useHasOffersFromMultipleTiers()

  const subType = hasTiers ? selectedOffer?.variant.displayNameSubtype : undefined

  const showStickyHeader = isCondensedProductHero || scrolledPast

  const isViewingOffers = step === 'viewOffers'
  const isShowingPrice = selectedOffer != null && isViewingOffers

  const productDisplayName = isViewingOffers ? productData.displayNameShort : productData.displayNameFull

  useEffect(() => {
    setIsCondensed(isCondensedProductHero)
  }, [isCondensedProductHero, setIsCondensed])

  useMotionValueEvent(scrollY, 'change', (latest) => {
    setScrolledPast(latest > HERO_TRESHOLD)
  })

  return (
    <div className={stickyProductHeader}>
      <div
        className={clsx(
          yStack({
            gap: 'md',
            paddingBlock: 'sm',
            paddingInline: 'md',
          }),
          stickyProductHeaderContent.base,
          showStickyHeader && stickyProductHeaderContent.visible,
          scrolledPast && stickyProductHeaderContent.shadow,
        )}
      >
        <div
          className={clsx(
            xStack({
              gap: 'sm',
              alignItems: 'center',
            }),
          )}
        >
          <Pillow size="small" {...productData.pillowImage} imagePriority={true} />

          <div className={sprinkles({ textAlign: 'left' })}>
            <Heading as="h1" variant="standard.18">
              <span>{productDisplayName}</span>
              {isShowingPrice && hasTiers && <span className={subTypeLabel}>{` ${subType}`}</span>}
            </Heading>

            {!isShowingPrice && (
              <Text color="textSecondary" size="md">
                {productData.purchaseFlowTagline}
              </Text>
            )}

            {isShowingPrice && <SelectedOfferAnimatedPrice />}
          </div>

          <ButtonNextLink
            className={mobileCloseButton}
            href={productData.pageLink}
            variant="secondary"
            Icon={<CrossIcon size="1.5rem" />}
            size="icon"
            aria-label={productDisplayName}
          />
        </div>

        <ProgressBar />
      </div>
    </div>
  )
}
